import React from 'react';
import PageHeader from '@/components/shared/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { accountingProfileApi } from '@/lib/api/profile';
import MobileOtpModal from '@/components/profile/MobileOtpModal';

function isValidIranNational10(d: string): boolean {
  return /^9\d{9}$/.test(d);
}

const ProfilePage: React.FC = () => {
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState('');
  const [ok, setOk] = React.useState('');
  const [name, setName] = React.useState('');
  const [username, setUsername] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [mobileNational, setMobileNational] = React.useState('');
  const [savedMobileNational, setSavedMobileNational] = React.useState('');

  const [otpOpen, setOtpOpen] = React.useState(false);
  const [otpCode, setOtpCode] = React.useState('');
  const [modalError, setModalError] = React.useState('');
  const [sendingOtp, setSendingOtp] = React.useState(false);
  const [resendingOtp, setResendingOtp] = React.useState(false);
  const [verifyingOtp, setVerifyingOtp] = React.useState(false);
  const [resendCooldown, setResendCooldown] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr('');
      try {
        const p = await accountingProfileApi.fetch();
        if (cancelled) return;
        setName(p.name || '');
        setUsername(p.username || '');
        setEmail(p.email || '');
        const nat =
          (p.mobile_national && /^9\d{9}$/.test(p.mobile_national) ? p.mobile_national : null) ??
          (p.mobile?.startsWith('09') ? p.mobile.slice(1) : '') ??
          '';
        const clean = nat.replace(/\D/g, '').slice(0, 10);
        setMobileNational(clean);
        setSavedMobileNational(clean);
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : 'خطا در دریافت پروفایل');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = window.setInterval(() => {
      setResendCooldown((s) => Math.max(0, s - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [resendCooldown]);

  const mobileChanged = mobileNational !== savedMobileNational;

  const requestOtp = async () => {
    setSendingOtp(true);
    setErr('');
    setOk('');
    try {
      const r = await accountingProfileApi.requestMobileOtp(mobileNational);
      if (r.alreadySet) {
        setSavedMobileNational(mobileNational);
        setOk(r.message ?? 'این شماره قبلا ثبت شده است.');
        return;
      }
      setOtpCode('');
      setOtpOpen(true);
      setResendCooldown(60);
      setOk(r.message ?? 'کد تایید ارسال شد.');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'ارسال کد ناموفق بود');
    } finally {
      setSendingOtp(false);
    }
  };

  const resendOtp = async () => {
    setResendingOtp(true);
    setModalError('');
    try {
      await accountingProfileApi.requestMobileOtp(mobileNational);
      setResendCooldown(60);
    } catch (e) {
      setModalError(e instanceof Error ? e.message : 'ارسال مجدد ناموفق بود');
    } finally {
      setResendingOtp(false);
    }
  };

  const verifyOtp = async () => {
    setVerifyingOtp(true);
    setModalError('');
    try {
      const p = await accountingProfileApi.verifyMobileOtp(otpCode);
      const nat =
        (p.mobile_national && /^9\d{9}$/.test(p.mobile_national) ? p.mobile_national : null) ??
        (p.mobile?.startsWith('09') ? p.mobile.slice(1) : '') ??
        '';
      const clean = nat.replace(/\D/g, '').slice(0, 10);
      setMobileNational(clean);
      setSavedMobileNational(clean);
      setOtpOpen(false);
      setOtpCode('');
      setOk('شماره موبایل تایید شد.');
    } catch (e) {
      setModalError(e instanceof Error ? e.message : 'تایید ناموفق بود');
    } finally {
      setVerifyingOtp(false);
    }
  };

  return (
    <div className="w-full min-w-0 space-y-6">
      <PageHeader
        title="پروفایل"
        description="تایید شماره موبایل با کد پیامکی (OTP) مشابه ماژول تسک‌منیجر."
      />

      <Card className="profile-loading-lock">
        <div className={loading ? 'profile-loading-blur' : ''}>
          <CardHeader>
            <CardTitle className="text-base">اطلاعات کاربر</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {err ? <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{err}</div> : null}
            {ok ? <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{ok}</div> : null}

            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div>
                <label className="mb-1 block text-xs text-slate-600">نام</label>
                <input value={name} readOnly className="h-10 w-full rounded-md border bg-slate-50 px-3 text-sm" />
              </div>
              <div>
                <label className="mb-1 block text-xs text-slate-600">نام کاربری</label>
                <input value={username} readOnly className="h-10 w-full rounded-md border bg-slate-50 px-3 text-sm" />
              </div>
              <div>
                <label className="mb-1 block text-xs text-slate-600">ایمیل</label>
                <input value={email} readOnly className="h-10 w-full rounded-md border bg-slate-50 px-3 text-sm" />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-xs text-slate-600">موبایل ایران (بعد از 98+)</label>
              <div className="flex max-w-sm overflow-hidden rounded-md border" dir="ltr">
                <span className="inline-flex items-center border-r bg-slate-50 px-3 text-sm text-slate-600">+98</span>
                <input
                  value={mobileNational}
                  onChange={(e) => setMobileNational(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  className="h-10 flex-1 px-3 text-sm outline-none"
                  placeholder="9123456789"
                />
              </div>
            </div>

            <div>
              <Button
                type="button"
                onClick={requestOtp}
                disabled={!isValidIranNational10(mobileNational) || !mobileChanged || sendingOtp || resendCooldown > 0}
              >
                {sendingOtp ? 'در حال ارسال...' : 'ارسال کد تایید'}
              </Button>
            </div>
          </CardContent>
        </div>

        {loading ? (
          <div className="profile-loading-overlay">
            <div className="flex items-center gap-2 text-sm text-slate-700">
              <Loader2 className="h-5 w-5 animate-spin" />
              در حال بارگذاری...
            </div>
          </div>
        ) : null}
      </Card>

      <MobileOtpModal
        open={otpOpen}
        phoneDisplay={`+98 ${mobileNational}`}
        code={otpCode}
        onCodeChange={setOtpCode}
        onClose={() => setOtpOpen(false)}
        onConfirm={verifyOtp}
        onResend={resendOtp}
        confirming={verifyingOtp}
        resending={resendingOtp}
        resendSecondsLeft={resendCooldown}
        modalError={modalError}
      />
    </div>
  );
};

export default ProfilePage;
